// Printer Services - QZ Tray and RawBT Integration
import qz from 'qz-tray';
import ReceiptFormatter from './receiptFormatter';

// QZ Tray Printer Service
export class QZTrayService {
  constructor() {
    this.isConnected = false;
    this.printerName = null;
  }

  async connect(printerName) {
    try {
      if (!qz.websocket.isActive()) {
        await qz.websocket.connect();
      }
      this.isConnected = true;
      this.printerName = printerName;
      console.log('QZ Tray connected successfully');
      return true;
    } catch (error) {
      console.error('Failed to connect to QZ Tray:', error);
      this.isConnected = false;
      throw new Error('Failed to connect to QZ Tray. Make sure QZ Tray is installed and running.');
    }
  }

  async disconnect() {
    try {
      if (qz.websocket.isActive()) {
        await qz.websocket.disconnect();
      }
      this.isConnected = false;
      this.printerName = null;
      console.log('QZ Tray disconnected');
    } catch (error) {
      console.error('Error disconnecting from QZ Tray:', error);
    }
  }

  async getPrinters() {
    try {
      if (!this.isConnected) {
        await this.connect();
      }
      const printers = await qz.printers.find();
      return printers;
    } catch (error) {
      console.error('Error getting printers:', error);
      throw new Error('Failed to get printer list');
    }
  }

  async printReceipt(receiptData, printerName = null) {
    try {
      const targetPrinter = printerName || this.printerName;
      if (!targetPrinter) {
        throw new Error('No printer specified');
      }

      if (!this.isConnected) {
        await this.connect(targetPrinter);
      }

      // Format receipt data for thermal printer using unified formatter
      const formattedData = ReceiptFormatter.formatForQZTray(receiptData);

      const config = qz.configs.create(targetPrinter);
      const data = [
        {
          type: 'raw',
          format: 'plain',
          data: formattedData
        }
      ];

      await qz.print(config, data);
      console.log('Receipt printed successfully via QZ Tray');
      return true;
    } catch (error) {
      console.error('Error printing receipt via QZ Tray:', error);
      throw new Error(`Failed to print receipt: ${error.message}`);
    }
  }

  async printOrderSlip(orderData, printerName = null) {
    try {
      const targetPrinter = printerName || this.printerName;
      if (!targetPrinter) {
        throw new Error('No printer specified');
      }

      if (!this.isConnected) {
        await this.connect(targetPrinter);
      }

      // Format order slip data for thermal printer using unified formatter
      const formattedData = ReceiptFormatter.formatOrderSlipForQZTray(orderData);

      const config = qz.configs.create(targetPrinter);
      const data = [
        {
          type: 'raw',
          format: 'plain',
          data: formattedData
        }
      ];

      await qz.print(config, data);
      console.log('Order slip printed successfully via QZ Tray');
      return true;
    } catch (error) {
      console.error('Error printing order slip via QZ Tray:', error);
      throw new Error(`Failed to print order slip: ${error.message}`);
    }
  }


}

// RawBT (URI Scheme) Printer Service
export class RawBTService {
  constructor() {
    this.isConnected = false;
  }

  async connect(deviceName = null, deviceAddress = null) {
    // RawBT URI scheme doesn't require actual connection - just mark as ready
    console.log('RawBT URI scheme mode - no Bluetooth connection needed');
    this.isConnected = true;
    return true;
  }

  async disconnect() {
    // No actual disconnection needed for URI scheme
    this.isConnected = false;
    console.log('RawBT URI scheme - marked as disconnected');
    return true;
  }

  async printReceipt(receiptData) {
    try {
      if (!this.isConnected) {
        throw new Error('RawBT service not ready');
      }

      // Format receipt data for thermal printer using unified formatter
      const formattedData = ReceiptFormatter.formatForRawBT(receiptData);
      
      // Print using RawBT URI scheme
      return this.printViaRawBTURI(formattedData);
    } catch (error) {
      console.error('Error printing receipt via RawBT:', error);
      throw new Error(`Failed to print receipt via RawBT: ${error.message}`);
    }
  }

  async printOrderSlip(orderData) {
    try {
      if (!this.isConnected) {
        throw new Error('RawBT service not ready');
      }

      // Format order slip data for thermal printer using unified formatter
      const formattedData = ReceiptFormatter.formatOrderSlipForRawBT(orderData);
      
      // Print using RawBT URI scheme
      return this.printViaRawBTURI(formattedData);
    } catch (error) {
      console.error('Error printing order slip via RawBT:', error);
      throw new Error(`Failed to print order slip via RawBT: ${error.message}`);
    }
  }

  // NEW: Print using RawBT app URI scheme
  printViaRawBTURI(formattedData) {
    try {
      console.log('Printing via RawBT URI scheme...');
      console.log('Original formatted data length:', formattedData.length);
      console.log('Original formatted data preview:', formattedData.substring(0, 100).replace(/[\x00-\x1F]/g, '[ESC]'));
      
      // Convert ESC/POS string directly to Base64 (simpler method)
      let base64Data;
      try {
        // Method 1: Direct btoa() - simpler and might work better
        base64Data = window.btoa(formattedData);
        console.log('Using direct btoa() encoding');
      } catch (error) {
        // Fallback: TextEncoder method for characters that btoa() can't handle
        console.log('Direct btoa() failed, using TextEncoder fallback');
        const encoder = new TextEncoder();
        const binaryData = encoder.encode(formattedData);
        base64Data = this.arrayBufferToBase64(binaryData);
      }
      
      console.log('Base64 data length:', base64Data.length);
      console.log('Base64 data preview:', base64Data.substring(0, 50) + '...');
      
      // Validate that we have actual data
      if (base64Data.length === 0) {
        throw new Error('Generated Base64 data is empty');
      }
      
      // Construct RawBT URI with correct MIME type
      const rawbtURI = `rawbt:data:text/plain;base64,${base64Data}`;
      
      console.log('Opening RawBT URI with data length:', base64Data.length);
      console.log('RawBT URI preview:', rawbtURI.substring(0, 100) + '...');
      
      // Open the RawBT URI - this will launch the RawBT app to handle printing
      window.location.href = rawbtURI;
      
      console.log('RawBT URI opened successfully');
      return true;
    } catch (error) {
      console.error('Error printing via RawBT URI:', error);
      throw new Error(`Failed to print via RawBT URI: ${error.message}`);
    }
  }

  // Helper function to convert ArrayBuffer to Base64
  // This is needed to encode the ESC/POS binary data for the RawBT URI scheme
  arrayBufferToBase64(buffer) {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  // Test method for debugging RawBT issues
  async testRawBTPrint() {
    try {
      console.log('Testing RawBT with minimal test data...');
      
      // Create test receipt data
      const testReceiptData = {
        transaction: {
          txn_no: 'TEST-001',
          date_created: new Date().toISOString(),
          cashier_name: 'Test Cashier',
          subtotal: 10.00,
          grand_total: 10.00,
          payment_total: 10.00,
          change: 0
        },
        transactionDetails: {
          details: [{
            product: { name: 'Test Item' },
            qty: 1,
            unit_price: 10.00
          }],
          payments: [{ payment_type: 'CASH' }]
        }
      };
      
      // Use unified formatter for test data
      const formattedData = ReceiptFormatter.formatForRawBT(testReceiptData);
      
      console.log('Test data length:', formattedData.length);
      console.log('Test data preview:', formattedData.substring(0, 50).replace(/[\x00-\x1F]/g, '[ESC]'));
      
      return this.printViaRawBTURI(formattedData);
    } catch (error) {
      console.error('RawBT test print failed:', error);
      throw error;
    }
  }
}

// Main Printer Service Manager
export class PrinterService {
  constructor() {
    this.qzTray = new QZTrayService();
    this.rawBT = new RawBTService();
    this.settings = null;
  }

  async loadSettings(forceReload = false) {
    try {
      // Only reload if we don't have settings cached or if forced
      if (this.settings && !forceReload) {
        console.log('Using cached printer settings:', this.settings);
        return this.settings;
      }
      
      console.log('Loading fresh printer settings from localStorage...');
      
      // Load settings from localStorage (same as POS/Settings pages)
      const printerType = localStorage.getItem('printer-type') || 'browser';
      const qzTrayEnabled = localStorage.getItem('qz-tray-enabled') === 'true';
      const qzTrayPrinterName = localStorage.getItem('qz-tray-printer-name') || null;
      const rawbtEnabled = localStorage.getItem('rawbt-enabled') === 'true';
      const rawbtDeviceName = localStorage.getItem('rawbt-device-name') || null;
      const rawbtDeviceAddress = localStorage.getItem('rawbt-device-address') || null;
      
      console.log('localStorage printer settings:');
      console.log('- printer-type:', printerType);
      console.log('- qz-tray-enabled:', qzTrayEnabled);
      console.log('- qz-tray-printer-name:', qzTrayPrinterName);
      console.log('- rawbt-enabled:', rawbtEnabled);
      console.log('- rawbt-device-name:', rawbtDeviceName);
      console.log('- rawbt-device-address:', rawbtDeviceAddress);
      
      this.settings = {
        printer_type: printerType,
        qz_tray_enabled: qzTrayEnabled,
        qz_tray_printer_name: qzTrayPrinterName,
        rawbt_enabled: rawbtEnabled,
        rawbt_device_name: rawbtDeviceName,
        rawbt_device_address: rawbtDeviceAddress
      };
      
      console.log('Final printer settings loaded from localStorage:', this.settings);
      console.log('Printer type detected:', this.settings.printer_type);
      return this.settings;
    } catch (error) {
      console.error('Failed to load printer settings from localStorage:', error);
      // Use default settings on error
      console.log('Error occurred, using default browser settings');
      this.settings = {
        printer_type: 'browser',
        qz_tray_enabled: false,
        qz_tray_printer_name: null,
        rawbt_enabled: false,
        rawbt_device_name: null,
        rawbt_device_address: null
      };
      return this.settings;
    }
  }

  // Method to clear cached settings and force reload
  clearSettingsCache() {
    console.log('Clearing printer settings cache...');
    this.settings = null;
  }

  // Method to manually test and debug settings (for browser console)
  async debugSettings() {
    console.log('=== PRINTER SERVICE DEBUG ===');
    console.log('Current cached settings:', this.settings);
    
    console.log('Loading fresh settings from localStorage...');
    const settings = await this.loadSettings(true);
    console.log('Fresh settings from localStorage:', settings);
    
    console.log('Testing localStorage directly...');
    try {
      const localStorageSettings = {
        'printer-type': localStorage.getItem('printer-type'),
        'qz-tray-enabled': localStorage.getItem('qz-tray-enabled'),
        'qz-tray-printer-name': localStorage.getItem('qz-tray-printer-name'),
        'rawbt-enabled': localStorage.getItem('rawbt-enabled'),
        'rawbt-device-name': localStorage.getItem('rawbt-device-name'),
        'rawbt-device-address': localStorage.getItem('rawbt-device-address')
      };
      console.log('Raw localStorage values:', localStorageSettings);
    } catch (error) {
      console.error('localStorage reading failed:', error);
    }
    
    console.log('=== END DEBUG ===');
    return settings;
  }

  async printReceipt(receiptData) {
    try {
      // Always reload settings to get the latest configuration
      console.log('Loading printer settings for receipt...');
      await this.loadSettings(true); // Force reload

      const printerType = this.settings.printer_type || 'browser';
      console.log('Receipt printer type:', printerType);
      console.log('Receipt printer settings:', this.settings);

      switch (printerType) {
        case 'qz_tray':
          console.log('Attempting QZ Tray printing...');
          if (this.settings.qz_tray_enabled && this.settings.qz_tray_printer_name) {
            console.log('Printing receipt via QZ Tray...');
            return await this.qzTray.printReceipt(receiptData, this.settings.qz_tray_printer_name);
          }
          throw new Error('QZ Tray is not configured properly');

        case 'rawbt':
          console.log('Attempting RawBT printing...');
          if (this.settings.rawbt_enabled) {
            console.log('Printing receipt via RawBT...');
            if (!this.rawBT.isConnected) {
              await this.rawBT.connect(this.settings.rawbt_device_name, this.settings.rawbt_device_address);
            }
            return await this.rawBT.printReceipt(receiptData);
          }
          throw new Error('RawBT is not configured properly');

        case 'browser':
        default:
          console.log('Browser printing requested - this should be handled by the UI component, not printer service');
          // For browser printing, throw a specific error that the UI can catch
          // This tells the UI to show the preview dialog instead
          throw new Error('BROWSER_PRINT_REQUESTED');
      }
    } catch (error) {
      console.log('Error in printReceipt:', error.message);
      if (error.message === 'BROWSER_PRINT_REQUESTED') {
        // Re-throw this specific error so UI can handle it
        throw error;
      }
      console.error('Error printing receipt:', error);
      // For other errors, also request browser print as fallback
      throw new Error('BROWSER_PRINT_REQUESTED');
    }
  }

  async printOrderSlip(orderData) {
    try {
      // Always reload settings to get the latest configuration
      console.log('Loading printer settings for order slip...');
      await this.loadSettings(true); // Force reload

      const printerType = this.settings.printer_type || 'browser';
      console.log('Order slip printer type:', printerType);
      console.log('Order slip printer settings:', this.settings);

      switch (printerType) {
        case 'qz_tray':
          if (this.settings.qz_tray_enabled && this.settings.qz_tray_printer_name) {
            console.log('Printing order slip via QZ Tray...');
            return await this.qzTray.printOrderSlip(orderData, this.settings.qz_tray_printer_name);
          }
          throw new Error('QZ Tray is not configured properly');

        case 'rawbt':
          if (this.settings.rawbt_enabled) {
            console.log('Printing order slip via RawBT...');
            if (!this.rawBT.isConnected) {
              await this.rawBT.connect(this.settings.rawbt_device_name, this.settings.rawbt_device_address);
            }
            return await this.rawBT.printOrderSlip(orderData);
          }
          throw new Error('RawBT is not configured properly');

        case 'browser':
        default:
          console.log('Browser printing requested - this should be handled by the UI component, not printer service');
          // For browser printing, throw a specific error that the UI can catch
          // This tells the UI to show the preview dialog instead
          throw new Error('BROWSER_PRINT_REQUESTED');
      }
    } catch (error) {
      if (error.message === 'BROWSER_PRINT_REQUESTED') {
        // Re-throw this specific error so UI can handle it
        throw error;
      }
      console.error('Error printing order slip:', error);
      // For other errors, also request browser print as fallback
      throw new Error('BROWSER_PRINT_REQUESTED');
    }
  }

  printViaBrowser(receiptData) {
    // Use the unified formatter for browser printing
    return new Promise((resolve) => {
      console.log('Printing via browser with unified formatter...', receiptData);
      
      // Generate formatted receipt content
      const formattedContent = ReceiptFormatter.formatForBrowserPreview(receiptData);
      
      // Create a new window for printing
      const printWindow = window.open("", "_blank");
      
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Receipt</title>
            <style>
              @page {
                size: 58mm auto;
                margin: 5mm 2mm;
              }
              body {
                font-family: 'Courier New', monospace;
                font-size: 12px;
                line-height: 1.2;
                margin: 0;
                padding: 0;
                background: white;
                color: black;
                width: 58mm;
              }
              .receipt-content {
                width: 100%;
                white-space: pre-wrap;
                word-break: break-word;
              }
              @media print {
                body {
                  -webkit-print-color-adjust: exact;
                  print-color-adjust: exact;
                }
              }
            </style>
          </head>
          <body>
            <div class="receipt-content">${formattedContent}</div>
          </body>
        </html>
      `);

      printWindow.document.close();
      printWindow.focus();

      // Wait for content to load, then print
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
        resolve(true);
      }, 500);
    });
  }

  printOrderSlipViaBrowser(orderData) {
    // Use the unified formatter for browser order slip printing
    return new Promise((resolve) => {
      console.log('Printing order slip via browser with unified formatter...', orderData);
      
      // Generate formatted order slip content
      const formattedContent = ReceiptFormatter.formatOrderSlipForBrowserPreview(orderData);
      
      // Create a new window for printing
      const printWindow = window.open("", "_blank");
      
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Order Slip - ${orderData.orderNumber || 'N/A'}</title>
            <style>
              @page {
                size: 58mm auto;
                margin: 5mm 2mm;
              }
              body {
                font-family: 'Courier New', monospace;
                font-size: 12px;
                line-height: 1.2;
                margin: 0;
                padding: 0;
                background: white;
                color: black;
                width: 58mm;
              }
              .order-content {
                width: 100%;
                white-space: pre-wrap;
                word-break: break-word;
              }
              @media print {
                body {
                  -webkit-print-color-adjust: exact;
                  print-color-adjust: exact;
                }
              }
            </style>
          </head>
          <body>
            <div class="order-content">${formattedContent}</div>
          </body>
        </html>
      `);

      printWindow.document.close();
      printWindow.focus();

      // Wait for content to load, then print
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
        resolve(true);
      }, 500);
    });
  }
}

// Export singleton instance
export const printerService = new PrinterService();

// Export debug function for browser console testing
window.debugPrinterService = () => printerService.debugSettings();
window.clearPrinterCache = () => printerService.clearSettingsCache();
window.testOrderSlipPrint = async () => {
  const testOrderData = {
    orderNumber: 'DEBUG-001',
    items: [{ name: 'Test Item', qty: 1, price: 10.00 }],
    total: 10.00
  };
  try {
    console.log('=== TESTING ORDER SLIP PRINT ===');
    await printerService.printOrderSlip(testOrderData);
  } catch (error) {
    console.log('Order slip print result:', error.message);
  }
};

// Debug function to verify printer type detection system
window.verifyPrinterSystem = async function() {
  console.log('\n=== PRINTER SYSTEM VERIFICATION ===');
  
  try {
    // Test settings loading
    console.log('1. Testing settings loading...');
    await printerService.loadSettings(true);
    console.log('✅ Settings loaded:', printerService.settings);
    
    // Test printer type detection
    console.log('\n2. Testing printer type detection...');
    const printerType = printerService.settings.printer_type || 'browser';
    console.log('✅ Detected printer type:', printerType);
    
    // Test ReceiptFormatter compatibility
    console.log('\n3. Testing ReceiptFormatter compatibility...');
    const sampleData = {
      id: 'TEST-001',
      total: 100.00,
      subtotal: 90.00,
      tax: 10.00,
      items: [{ name: 'Test Item', price: 90.00, quantity: 1 }],
      paymentMethods: [{ method: 'Cash', amount: 100.00 }],
      date: new Date().toISOString()
    };
    
    const formatter = new ReceiptFormatter(printerType);
    const receiptContent = formatter.generateReceipt(sampleData);
    console.log('✅ ReceiptFormatter working with printer type:', printerType);
    
    // Test static helper methods
    console.log('\n4. Testing static helper methods...');
    const browserFormat = ReceiptFormatter.formatForBrowserPreview(sampleData);
    const qzFormat = ReceiptFormatter.formatForQZTray(sampleData);
    const rawbtFormat = ReceiptFormatter.formatForRawBT(sampleData);
    console.log('✅ All static helper methods working');
    
    console.log('\n✅ PRINTER SYSTEM VERIFICATION COMPLETE - ALL TESTS PASSED ✅');
    return { success: true, printerType, settings: printerService.settings };
  } catch (error) {
    console.error('❌ PRINTER SYSTEM VERIFICATION FAILED:', error);
    return { success: false, error: error.message };
  }
};

console.log('🔧 Added debugPrinterService(), clearPrinterCache(), testOrderSlipPrint(), and verifyPrinterSystem() functions to window for debugging')